import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { SearchInput } from '../components/ui/SearchInput';
import { RefreshCw, Download } from 'lucide-react';
import { CountBand } from '../components/ui/CountBand';

const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
const tok = () =>
  localStorage.getItem('intrlock_token') ||
  localStorage.getItem('jwt') ||
  localStorage.getItem('token') ||
  '';

interface UnlockEvent {
  id: string;
  occurred_at: string;
  event_type?: string | null;
  method?: string | null;
  success?: boolean | null;
  reason?: string | null;
  person_id?: string | null;
  person_name?: string | null;
  property_id?: string | null;
  property_name?: string | null;
  device_id?: string | null;
  device_name?: string | null;
  unit_number?: string | null;
  call_id?: string | null;
  digit?: string | null;
  // Guest / delivery PIN attribution + capture
  guest_pin_id?: string | null;
  actor_name?: string | null;
  actor_type?: string | null;
  has_entry_photo?: boolean | null;
}

const normalizeMethod = (value?: string | null): string => {
  const method = String(value || '').trim().toLowerCase().replace(/[\s.-]+/g, '_');
  const aliases: Record<string, string> = {
    app: 'mobile', mobile_app: 'mobile', ios: 'mobile', android: 'mobile',
    web_app: 'web', dashboard: 'web', portal: 'visitor_pass',
    face_recognition: 'face', facial_recognition: 'face',
    bluetooth: 'ble', bluetooth_le: 'ble', keypad: 'pin', pin_code: 'pin',
    telephone: 'dtmf', phone: 'dtmf', remote_unlock: 'remote',
  };
  return aliases[method] || method || 'system';
};

const methodLabel: Record<string, string> = {
  app: 'Mobile App',
  dtmf: 'DTMF',
  pin: 'PIN code',
  face: 'Face',
  face_recognition: 'Face',
  remote: 'Remote',
  guest_pin: 'Guest PIN',
  ble: 'Bluetooth',
  visitor_pass: 'Visitor Pass',
  kiosk: 'Kiosk',
  web: 'Web App',
  api: 'API',
  card: 'Access Card',
  mobile: 'Mobile App',
  system: 'System',
};

const eventTypeLabel = (value?: string | null): string => {
  const type = String(value || '').trim().toLowerCase().replace(/[\s.-]+/g, '_');
  if (/door_(unlocked|unlock_succeeded)|unlock_(succeeded|completed)|access_granted/.test(type)) return 'Door Unlocked';
  if (/door_locked|lock_succeeded/.test(type)) return 'Door Locked';
  if (/denied|failed|rejected/.test(type)) return 'Access Denied';
  if (/face.*(match|verified)/.test(type)) return 'Identity Verified';
  if (/credential.*created|pin.*created|code.*issued/.test(type)) return 'Access Code Created';
  if (/credential.*revoked|pin.*revoked|code.*revoked/.test(type)) return 'Access Code Revoked';
  return type ? type.split('_').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ') : 'Device Event';
};

const formatDateTime = (iso: string) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const eventResult = (event: UnlockEvent): { label: string; color: 'green' | 'red' | 'blue' | 'gray' } => {
  const type = String(event.event_type || '').toLowerCase();
  if (event.success === false || /denied|failed|rejected/.test(type)) return { label: event.reason ? `Denied: ${event.reason}` : 'Denied', color: 'red' };
  if (/door_unlocked|unlock_succeeded|access_granted/.test(type)) return { label: /access_granted/.test(type) ? 'Granted' : 'Unlocked', color: 'green' };
  if (/door_locked/.test(type)) return { label: 'Locked', color: 'blue' };
  if (/face.*(match|verif)|verified/.test(type)) return { label: 'Verified', color: 'green' };
  if (/created|issued|added/.test(type)) return { label: 'Created', color: 'blue' };
  if (/revoked|removed|deleted|expired/.test(type)) return { label: 'Revoked', color: 'gray' };
  return { label: 'Completed', color: 'green' };
};

type SortCol = 'occurred_at' | 'person_name' | 'device_name' | 'method' | 'result';
type SortDir = 'asc' | 'desc';
type DateFilter = 'today' | 'week' | 'month' | 'all';

const PAGE_SIZE = 50;

interface DeviceHistoryProps {
  /** When set, scopes the feed to one property and skips the page header.
   *  Used by PropertyDetail to embed the same component inside a tab. */
  presetPropertyId?: string | number;
  hidePageHeader?: boolean;
}

export const DeviceHistory: React.FC<DeviceHistoryProps> = ({ presetPropertyId, hidePageHeader }) => {
  const [events, setEvents] = useState<UnlockEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState<string>('');
  const [resultFilter, setResultFilter] = useState<'all' | 'unlocked' | 'failed'>('all');
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [error, setError] = useState<string | null>(null);
  const [sortCol, setSortCol] = useState<SortCol>('occurred_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(0);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const [hasMoreColumns, setHasMoreColumns] = useState(false);
  // Lazy-loaded thumbnails keyed by guest_pin_id. 'loading' is a placeholder
  // while the fetch is in flight; null = no photo (404); string = base64 jpg.
  const [photoCache, setPhotoCache] = useState<Record<string, 'loading' | null | string>>({});
  const [photoModal, setPhotoModal] = useState<{ src: string; capturedAt?: string | null; who?: string | null } | null>(null);

  const fetchPhoto = (guestPinId: string) => {
    if (photoCache[guestPinId] !== undefined) return;
    setPhotoCache((m) => ({ ...m, [guestPinId]: 'loading' }));
    fetch(`${API}/intercom/guest-pin/${encodeURIComponent(guestPinId)}/entry-photo`, {
      headers: { Authorization: `Bearer ${tok()}` },
    })
      .then(async (r) => {
        if (r.status === 404) return null;
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => {
        setPhotoCache((m) => ({ ...m, [guestPinId]: j?.photo_base64 ? `data:image/jpeg;base64,${j.photo_base64}` : null }));
      })
      .catch(() => setPhotoCache((m) => ({ ...m, [guestPinId]: null })));
  };

  const loadEvents = useCallback((silent = false) => {
    const headers = { Authorization: `Bearer ${tok()}` };
    if (!silent) setLoading(true);
    const qs = presetPropertyId
      ? `?property_id=${encodeURIComponent(String(presetPropertyId))}&limit=500`
      : '?limit=500';
    fetch(`${API}/acs-systems/unlock-events${qs}`, { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then((j) => {
        const items: UnlockEvent[] = j.items ?? [];
        setEvents(items);
        setError(null);
        if (!silent) setPage(0);
        // Pre-fetch thumbnails for the first ~60 events that have a photo.
        const seen = new Set<string>();
        for (const e of items.slice(0, 60)) {
          if (e.has_entry_photo && e.guest_pin_id && !seen.has(e.guest_pin_id)) {
            seen.add(e.guest_pin_id);
            fetchPhoto(e.guest_pin_id);
          }
        }
      })
      .catch((e: any) => setError(e?.message ?? String(e)))
      .finally(() => { if (!silent) setLoading(false); });
  }, [presetPropertyId]); // eslint-disable-line

  useEffect(() => {
    loadEvents();
    const timer = window.setInterval(() => loadEvents(true), 15_000);
    return () => window.clearInterval(timer);
  }, [loadEvents]);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [search, methodFilter, resultFilter, dateFilter, sortCol, sortDir]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = events.filter((e) => {
      if (methodFilter && normalizeMethod(e.method) !== methodFilter) return false;
      if (resultFilter === 'unlocked' && e.success === false) return false;
      if (resultFilter === 'failed' && e.success !== false) return false;

      // Date filter
      if (dateFilter !== 'all') {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const weekAgo = new Date(today); weekAgo.setDate(weekAgo.getDate() - 7);
        const monthAgo = new Date(today); monthAgo.setMonth(monthAgo.getMonth() - 1);
        const t = new Date(e.occurred_at);
        if (dateFilter === 'today' && t < today) return false;
        if (dateFilter === 'week' && t < weekAgo) return false;
        if (dateFilter === 'month' && t < monthAgo) return false;
      }

      if (!q) return true;
      const hay = [
        e.person_name, e.device_name, e.property_name, e.unit_number,
        e.method, e.reason, e.event_type, e.actor_name,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });

    // Sort
    result = [...result].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';
      if (sortCol === 'occurred_at') {
        aVal = new Date(a.occurred_at).getTime();
        bVal = new Date(b.occurred_at).getTime();
      } else if (sortCol === 'person_name') {
        aVal = (a.actor_name || a.person_name || '').toLowerCase();
        bVal = (b.actor_name || b.person_name || '').toLowerCase();
      } else if (sortCol === 'device_name') {
        aVal = (a.device_name || '').toLowerCase();
        bVal = (b.device_name || '').toLowerCase();
      } else if (sortCol === 'method') {
        aVal = normalizeMethod(a.method);
        bVal = normalizeMethod(b.method);
      } else if (sortCol === 'result') {
        aVal = a.success === false ? 0 : 1;
        bVal = b.success === false ? 0 : 1;
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [events, search, methodFilter, resultFilter, dateFilter, sortCol, sortDir]);

  const methods = useMemo(() => {
    const set = new Set<string>();
    for (const e of events) set.add(normalizeMethod(e.method));
    return Array.from(set).sort();
  }, [events]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = useMemo(
    () => filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [filtered, page]
  );

  const measureTable = useCallback(() => {
    const node = tableScrollRef.current;
    setHasMoreColumns(Boolean(node && node.scrollWidth - node.clientWidth > 2 && node.scrollLeft + node.clientWidth < node.scrollWidth - 2));
  }, []);

  useEffect(() => {
    const node = tableScrollRef.current;
    if (!node) return;
    measureTable();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measureTable);
    observer.observe(node);
    const table = node.querySelector('table');
    if (table) observer.observe(table);
    return () => observer.disconnect();
  }, [loading, error, paged.length, measureTable]);

  const toggleSort = (col: SortCol) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir(col === 'occurred_at' ? 'desc' : 'asc');
    }
  };

  const SortIndicator = ({ col }: { col: SortCol }) => {
    if (sortCol !== col) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-brand-600 ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const exportCSV = () => {
    const cols = ['When', 'Event Type', 'Person', 'Actor Type', 'Unit', 'Device', 'Property', 'Method', 'Result', 'Reason'];
    const rows = filtered.map(ev => [
      ev.occurred_at,
      eventTypeLabel(ev.event_type),
      ev.actor_name || ev.person_name || '',
      ev.actor_type || '',
      ev.unit_number || '',
      ev.device_name || '',
      ev.property_name || '',
      methodLabel[normalizeMethod(ev.method)] ?? eventTypeLabel(normalizeMethod(ev.method)),
      eventResult(ev).label,
      ev.reason || '',
    ]);
    const csv = [cols, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `device-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const dateBtns: { label: string; val: DateFilter }[] = [
    { label: 'Today', val: 'today' },
    { label: 'This Week', val: 'week' },
    { label: 'This Month', val: 'month' },
    { label: 'All', val: 'all' },
  ];

  return (
    <div className={hidePageHeader ? '' : 'shadow-list-page prototype-page history-page'}>
      {!hidePageHeader && (
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Device History</h1>
            <p className="text-gray-500 mt-1">Every unlock event you have visibility into — who opened what door, when, and how.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={exportCSV}
              disabled={filtered.length === 0}
              className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              type="button"
              onClick={() => loadEvents()}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      )}

      {!hidePageHeader && <CountBand value={filtered.length} detail="Device events in the current accessible portfolio" />}
      {events.length >= 500 && <p role="status" className="mb-3 border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">Showing the newest 500 device events. Narrow the filters before exporting to avoid an incomplete result.</p>}

      <Card padding={false}>
        {/* Filter bar */}
        <div className="prototype-toolbar history-toolbar p-4 border-b border-gray-100 space-y-3">
          <div className="flex flex-wrap gap-3 items-center">
            <SearchInput value={search} onChange={setSearch} placeholder="Search by person, device, unit, property…" />
            <select
              value={methodFilter}
              onChange={(e) => setMethodFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1.5"
            >
              <option value="">All methods</option>
              {methods.map((m) => <option key={m} value={m}>{methodLabel[m] ?? m}</option>)}
            </select>
            <select
              value={resultFilter}
              onChange={(e) => setResultFilter(e.target.value as any)}
              className="text-sm border border-gray-300 rounded px-2 py-1.5"
            >
              <option value="all">All results</option>
              <option value="unlocked">Successful</option>
              <option value="failed">Denied / Failed</option>
            </select>
            {/* Export + Refresh when header is hidden (embedded mode) */}
            {hidePageHeader && (
              <>
                <button
                  type="button"
                  onClick={exportCSV}
                  disabled={filtered.length === 0}
                  className="flex items-center gap-1 px-2 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
                >
                  <Download className="w-3 h-3" /> CSV
                </button>
                <button
                  type="button"
                  onClick={() => loadEvents()}
                  disabled={loading}
                  className="flex items-center gap-1 px-2 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
                >
                  <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </>
            )}
            <span className="ml-auto text-xs text-gray-500">{filtered.length} of {events.length} events</span>
          </div>
          {/* Date filter row */}
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-gray-500">Date:</span>
            {dateBtns.map(({ label, val }) => (
              <button
                key={val}
                type="button"
                onClick={() => setDateFilter(val)}
                className={`px-3 py-1.5 text-xs rounded-lg ${
                  dateFilter === val
                    ? 'bg-brand-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading…</div>
        ) : error ? (
          <div className="p-4 text-sm text-red-600 bg-red-50 border-t border-red-200">Failed to load: {error}</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No events match the current filters.</div>
        ) : (
          <div>
          {hasMoreColumns && (
            <button
              type="button"
              className="intrlock-table-overflow-hint"
              onClick={() => tableScrollRef.current?.scrollBy({ left: Math.max(240, tableScrollRef.current.clientWidth * 0.8), behavior: 'smooth' })}
              aria-label="More device history columns are available. Scroll right"
            >
              More columns are available. Scroll right <span aria-hidden="true">→</span>
            </button>
          )}
          <div ref={tableScrollRef} className="overflow-x-auto" onScroll={measureTable}>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-center text-xs text-gray-900 font-bold uppercase tracking-wider border-b border-gray-200 whitespace-nowrap">
                <tr>
                  <th className="py-2 px-4">Photo</th>
                  <th
                    className="py-2 px-4 cursor-pointer hover:text-gray-800 select-none"
                    onClick={() => toggleSort('occurred_at')}
                  >
                    When <SortIndicator col="occurred_at" />
                  </th>
                  <th
                    className="py-2 px-4 cursor-pointer hover:text-gray-800 select-none"
                    onClick={() => toggleSort('person_name')}
                  >
                    Person <SortIndicator col="person_name" />
                  </th>
                  <th
                    className="py-2 px-4 cursor-pointer hover:text-gray-800 select-none"
                    onClick={() => toggleSort('device_name')}
                  >
                    Device <SortIndicator col="device_name" />
                  </th>
                  <th className="py-2 px-4">Property</th>
                  <th className="py-2 px-4">Event Type</th>
                  <th
                    className="py-2 px-4 cursor-pointer hover:text-gray-800 select-none"
                    onClick={() => toggleSort('method')}
                  >
                    Method <SortIndicator col="method" />
                  </th>
                  <th
                    className="py-2 px-4 cursor-pointer hover:text-gray-800 select-none"
                    onClick={() => toggleSort('result')}
                  >
                    Result <SortIndicator col="result" />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-center">
                {paged.map((ev) => {
                  const cached = ev.guest_pin_id ? photoCache[ev.guest_pin_id] : undefined;
                  const personDisplay = ev.actor_name || ev.person_name;
                  const actorBadge = ev.actor_type === 'guest' ? ' (Guest)'
                    : ev.actor_type === 'delivery' ? ' (Delivery)'
                    : '';
                  const result = eventResult(ev);
                  return (
                  <tr key={ev.id} className="hover:bg-gray-50">
                    <td className="py-2 px-4 w-14">
                      {ev.has_entry_photo && ev.guest_pin_id ? (
                        cached === 'loading' ? (
                          <div className="w-10 h-10 rounded bg-gray-100 animate-pulse" />
                        ) : typeof cached === 'string' ? (
                          <button
                            type="button"
                            onClick={() => setPhotoModal({ src: cached, capturedAt: ev.occurred_at, who: personDisplay })}
                            className="block w-10 h-10 rounded overflow-hidden border border-gray-200 hover:ring-2 hover:ring-accent-500 focus:outline-none"
                            title="View entry photo"
                          >
                            <img src={cached} alt="" className="w-full h-full object-cover" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => ev.guest_pin_id && fetchPhoto(ev.guest_pin_id)}
                            className="text-xs text-accent-600 hover:underline"
                          >📷</button>
                        )
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="py-2 px-4 text-xs text-gray-700 whitespace-nowrap">{formatDateTime(ev.occurred_at)}</td>
                    <td className="py-2 px-4">
                      <p className="text-sm font-medium text-gray-900">
                        {personDisplay
                          ? <>{personDisplay}<span className="text-gray-500">{actorBadge}</span></>
                          : (ev.person_id ? <span className="text-gray-500">person {String(ev.person_id).slice(0, 8)}</span> : <span className="text-gray-400">—</span>)
                        }
                      </p>
                      {ev.unit_number && <p className="text-[11px] text-gray-500">Unit {ev.unit_number}</p>}
                    </td>
                    <td className="py-2 px-4 text-sm text-gray-700">{ev.device_name || <span className="text-gray-400">—</span>}</td>
                    <td className="py-2 px-4 text-sm text-gray-700">{ev.property_name || <span className="text-gray-400">—</span>}</td>
                    <td className="py-2 px-4 text-xs text-gray-700">
                      {ev.event_type
                        ? <span className="text-[11px] bg-gray-50 border border-gray-200 rounded px-2 py-0.5">{eventTypeLabel(ev.event_type)}</span>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="py-2 px-4 text-xs">
                      <Badge color="blue">{methodLabel[normalizeMethod(ev.method)] ?? eventTypeLabel(normalizeMethod(ev.method))}</Badge>
                    </td>
                    <td className="py-2 px-4 text-xs">
                      <Badge color={result.color}>{result.label}</Badge>
                      {ev.call_id && (
                        <a
                          href="/call-history"
                          className="block text-[10px] text-brand-600 hover:underline mt-0.5"
                          title={`Call ${String(ev.call_id).slice(0, 8)}`}
                        >
                          → call
                        </a>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
            <span>
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage(0)}
                className="px-2 py-1.5 rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50 text-xs"
              >«</button>
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                className="px-3 py-1.5 rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >Previous</button>
              <span className="px-2">Page {page + 1} of {pageCount}</span>
              <button
                type="button"
                disabled={page >= pageCount - 1}
                onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                className="px-3 py-1.5 rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >Next</button>
              <button
                type="button"
                disabled={page >= pageCount - 1}
                onClick={() => setPage(pageCount - 1)}
                className="px-2 py-1.5 rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50 text-xs"
              >»</button>
            </div>
          </div>
        )}
      </Card>

      {photoModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setPhotoModal(null)}
        >
          <div className="bg-white rounded-xl overflow-hidden max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900">Entry photo</p>
                {photoModal.who && <p className="text-xs text-gray-500">{photoModal.who}</p>}
                {photoModal.capturedAt && <p className="text-[11px] text-gray-400">{formatDateTime(photoModal.capturedAt)}</p>}
              </div>
              <button
                type="button"
                onClick={() => setPhotoModal(null)}
                className="text-gray-400 hover:text-gray-700 text-xl leading-none"
              >×</button>
            </div>
            <img src={photoModal.src} alt="Entry photo" className="w-full h-auto block" />
          </div>
        </div>
      )}
    </div>
  );
};
